import type { GameEvent, PuzzleModel } from '../types.ts';

/**
 * FLOW — the chain reaction of solving: solve a word, its letters land in crossing
 * answers, one of those gets easier, solve it, and so on.
 *
 * Experimental and deliberately isolated: it only consumes `GameEvent`s plus the model,
 * never changes game state, and nothing in the core engine imports it. It rewards
 * cascades, not speed — the inactivity limit is generous and only exists so a chain
 * from an earlier sitting doesn't silently resume.
 */

export interface FlowConfig {
	/** A solve more than this long after the previous one starts a new chain. */
	inactivityMs: number;
	/** The UI shows `FLOW ×n` only once `current >= minChain`. */
	minChain: number;
}

export const DEFAULT_FLOW_CONFIG: FlowConfig = { inactivityMs: 180_000, minChain: 2 };

export interface FlowState {
	/** Answer ids in the current chain, in solve order. */
	chain: string[];
	/** Length of the current chain (`chain.length`). */
	current: number;
	/** Longest chain seen this puzzle. */
	best: number;
	/** `at` of the last counted solve, or null. */
	lastSolveAt: number | null;
	/** Unsolved answers that the chain has opened (received a locked letter from it). */
	frontier: string[];
	/**
	 * Every chain this puzzle, in solve order (the current chain is the last one). This
	 * is the solve's shape: the share text draws it as arrows.
	 */
	segments: string[][];
}

export type FlowLogKind = 'start' | 'continue' | 'break' | 'ignore';

export interface FlowLogEntry {
	at: number;
	kind: FlowLogKind;
	/** The answer the decision is about; null for a break with no solve (`tickFlow`). */
	answerId: string | null;
	/** Human-readable explanation for the dev panel. */
	reason: string;
	/** The chain after this decision. */
	chain: string[];
}

export interface FlowResult {
	flow: FlowState;
	log: FlowLogEntry[];
}

export function createFlow(): FlowState {
	return { chain: [], current: 0, best: 0, lastSolveAt: null, frontier: [], segments: [] };
}

function secs(ms: number): string {
	const s = ms / 1000;
	return `${Number.isInteger(s) ? s : s.toFixed(1)}s`;
}

function resolveConfig(config?: Partial<FlowConfig>): FlowConfig {
	return { ...DEFAULT_FLOW_CONFIG, ...config };
}

function isStale(flow: FlowState, now: number, config: FlowConfig): boolean {
	return flow.chain.length > 0 && flow.lastSolveAt !== null && now - flow.lastSolveAt > config.inactivityMs;
}

function broken(flow: FlowState): FlowState {
	return { ...flow, chain: [], current: 0, frontier: [] };
}

/**
 * Feeds one game event into Flow. Only `answerSolved` is a decision; other events
 * leave Flow unchanged and log nothing (letters typed don't matter — only solves do).
 */
export function applyFlowEvent(
	model: PuzzleModel,
	flow: FlowState,
	event: GameEvent,
	config?: Partial<FlowConfig>,
): FlowResult {
	if (event.type !== 'answerSolved') return { flow, log: [] };
	const cfg = resolveConfig(config);
	const id = event.answerId;
	const now = event.at;
	const log: FlowLogEntry[] = [];

	if (event.source === 'reveal' || event.source === 'solveAll') {
		const how = event.source === 'reveal' ? 'was revealed' : 'was auto-completed (solveAll)';
		log.push({
			at: now,
			kind: 'ignore',
			answerId: id,
			reason: `${id} ${how}, not solved — it neither extends nor breaks the chain`,
			chain: flow.chain,
		});
		return { flow, log };
	}
	if (flow.chain.includes(id)) {
		log.push({ at: now, kind: 'ignore', answerId: id, reason: `${id} is already in the chain`, chain: flow.chain });
		return { flow, log };
	}

	let base = flow;
	if (isStale(flow, now, cfg)) {
		const gap = now - flow.lastSolveAt!;
		base = broken(flow);
		log.push({
			at: now,
			kind: 'break',
			answerId: id,
			reason: `${secs(gap)} since the last solve (limit ${secs(cfg.inactivityMs)}): chain of ${flow.current} ended by inactivity`,
			chain: [],
		});
	}

	const crossings = model.crossings[id] ?? [];
	const linkedTo = base.chain.filter((other) => crossings.includes(other));

	if (base.chain.length > 0 && linkedTo.length > 0) {
		const chain = [...base.chain, id];
		const frontier = [...new Set([...base.frontier, ...event.opened])].filter((a) => a !== id && !chain.includes(a));
		const next: FlowState = {
			chain,
			current: chain.length,
			best: Math.max(base.best, chain.length),
			lastSolveAt: now,
			frontier,
			segments: [...base.segments.slice(0, -1), [...(base.segments.at(-1) ?? []), id]],
		};
		const gap = base.lastSolveAt === null ? 0 : now - base.lastSolveAt;
		log.push({
			at: now,
			kind: 'continue',
			answerId: id,
			reason: `${id} crosses ${linkedTo.join(', ')} (in the chain), solved ${secs(gap)} after the previous solve: FLOW ×${chain.length}`,
			chain,
		});
		return { flow: next, log };
	}

	if (base.chain.length > 0) {
		log.push({
			at: now,
			kind: 'break',
			answerId: id,
			reason: `${id} crosses nothing in the chain [${base.chain.join(', ')}]: chain of ${base.current} ended`,
			chain: [],
		});
	}
	const chain = [id];
	const next: FlowState = {
		chain,
		current: 1,
		best: Math.max(base.best, 1),
		lastSolveAt: now,
		frontier: event.opened.filter((a) => a !== id),
		segments: [...base.segments, [id]],
	};
	const opened = event.opened.length > 0 ? `opens ${event.opened.join(', ')}` : 'opens nothing new';
	log.push({ at: now, kind: 'start', answerId: id, reason: `${id} solved: new chain of 1, ${opened}`, chain });
	return { flow: next, log };
}

/** Folds a batch of events (e.g. one `Transition`'s events) into Flow. */
export function applyFlowEvents(
	model: PuzzleModel,
	flow: FlowState,
	events: readonly GameEvent[],
	config?: Partial<FlowConfig>,
): FlowResult {
	let current = flow;
	const log: FlowLogEntry[] = [];
	for (const event of events) {
		const r = applyFlowEvent(model, current, event, config);
		current = r.flow;
		log.push(...r.log);
	}
	return { flow: current, log };
}

/**
 * Ends a chain that has gone stale without waiting for the next solve, so the UI can
 * drop the `FLOW ×n` badge after a long pause. Call it on a timer or when the page
 * becomes visible. No-op (and no log) if the chain is still live.
 */
export function tickFlow(flow: FlowState, now: number, config?: Partial<FlowConfig>): FlowResult {
	const cfg = resolveConfig(config);
	if (!isStale(flow, now, cfg)) return { flow, log: [] };
	const gap = now - flow.lastSolveAt!;
	return {
		flow: broken(flow),
		log: [
			{
				at: now,
				kind: 'break',
				answerId: null,
				reason: `${secs(gap)} without a solve (limit ${secs(cfg.inactivityMs)}): chain of ${flow.current} ended by inactivity`,
				chain: [],
			},
		],
	};
}

/**
 * Flow's inactivity window counts time at the puzzle, not time away: a pause from
 * `pausedAt` to `now` (the app hidden, the phone locked) moves the chain's last solve
 * forward by the gap, so a break never breaks a Flow. Call it on resume, before
 * {@link tickFlow}. Returns the same object when there is nothing to shift: no chain
 * timestamp, no time away, or a pause from before the last solve.
 */
export function discountAway(flow: FlowState, pausedAt: number, now: number): FlowState {
	if (flow.lastSolveAt === null || !(now > pausedAt) || pausedAt < flow.lastSolveAt) return flow;
	return { ...flow, lastSolveAt: flow.lastSolveAt + (now - pausedAt) };
}

/** The number to show as `FLOW ×n`, or null when the chain is below `minChain`. */
export function visibleFlow(flow: FlowState, config?: Partial<FlowConfig>): number | null {
	return flow.current >= resolveConfig(config).minChain ? flow.current : null;
}

const isStrArr = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** Defensive parse of a persisted `FlowState`; anything malformed gives a fresh one. Never throws. */
export function restoreFlow(raw: unknown): FlowState {
	try {
		if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return createFlow();
		const r = raw as Record<string, unknown>;
		if (!isStrArr(r.chain) || !isStrArr(r.frontier) || !isCount(r.best)) return createFlow();
		if (!(r.lastSolveAt === null || (typeof r.lastSolveAt === 'number' && Number.isFinite(r.lastSolveAt)))) {
			return createFlow();
		}
		const chain = [...r.chain];
		// Older saves predate segments; start the record from the live chain.
		const segments = Array.isArray(r.segments) && r.segments.every(isStrArr)
			? r.segments.map((seg) => [...seg])
			: chain.length ? [chain] : [];
		return {
			chain,
			current: chain.length,
			best: Math.max(r.best, chain.length),
			lastSolveAt: r.lastSolveAt,
			frontier: [...r.frontier],
			segments,
		};
	} catch {
		return createFlow();
	}
}
